import React, { useEffect, useRef, useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import Reveal from "./Reveal";

const CATEGORIES = ["All", "Nature", "City", "Parks"];
const DEFAULT_UPLOAD_CATEGORY = CATEGORIES.find((c) => c !== "All");
const EMPTY_PHOTO = { title: "", description: "", category: DEFAULT_UPLOAD_CATEGORY, url: "", file: null };

const altFor = (photo) =>
  photo.title || (photo.category ? `${photo.category} photo` : "Travel photo");

// Keeps casual right-click / drag saving off the photos, as before.
const protectImage = {
  onContextMenu: (e) => e.preventDefault(),
  onDragStart: (e) => e.preventDefault(),
  style: { userSelect: "none" },
};

// Declared at module level so its form state survives re-renders of the page.
const UploadPhotoModal = ({ open, onClose, onSave }) => {
  const [newPhoto, setNewPhoto] = useState(EMPTY_PHOTO);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const close = () => {
    if (isUploading) return;
    setNewPhoto(EMPTY_PHOTO);
    setError("");
    onClose();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) setNewPhoto({ ...newPhoto, url: file.name, file });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsUploading(true);
    try {
      const result = await onSave(newPhoto);
      if (result?.success === false) {
        setError("Upload failed: " + (result.error || "please try again."));
        return;
      }
      setNewPhoto(EMPTY_PHOTO);
      onClose();
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={close}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-title"
        className="modal-panel max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" onClick={close} className="icon-btn absolute right-4 top-4" aria-label="Close">
          <X size={18} strokeWidth={1.75} />
        </button>
        <h2 id="upload-title" className="text-xl font-semibold text-slate-50">Upload Photo</h2>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          <div>
            <label htmlFor="photo-file" className="field-label">Photo file</label>
            <input
              id="photo-file"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              required
              disabled={isUploading}
              className="block w-full text-sm text-slate-300 file:mr-4 file:rounded-full file:border-0 file:bg-slate-800 file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-100 hover:file:bg-slate-700"
            />
          </div>

          <div>
            <label htmlFor="photo-title" className="field-label">Title</label>
            <input
              id="photo-title"
              type="text"
              value={newPhoto.title}
              onChange={(e) => setNewPhoto({ ...newPhoto, title: e.target.value })}
              placeholder="Photo title"
              required
              disabled={isUploading}
              className="field"
            />
          </div>

          <div>
            <label htmlFor="photo-description" className="field-label">Description</label>
            <textarea
              id="photo-description"
              value={newPhoto.description}
              onChange={(e) => setNewPhoto({ ...newPhoto, description: e.target.value })}
              placeholder="Photo description"
              rows="3"
              disabled={isUploading}
              className="field-area"
            />
          </div>

          <div>
            <label htmlFor="photo-category" className="field-label">Category</label>
            <select
              id="photo-category"
              value={newPhoto.category}
              onChange={(e) => setNewPhoto({ ...newPhoto, category: e.target.value })}
              disabled={isUploading}
              className="field appearance-none"
            >
              {CATEGORIES.filter((cat) => cat !== "All").map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {error && <p role="alert" className="form-alert">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={close} disabled={isUploading} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={isUploading} className="btn-primary flex-1">
              {isUploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Lightbox = ({ photo, onClose }) => {
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={altFor(photo)}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/95 p-4"
      onClick={onClose}
    >
      <button ref={closeRef} type="button" onClick={onClose} className="icon-btn absolute right-4 top-4" aria-label="Close">
        <X size={18} strokeWidth={1.75} />
      </button>
      <figure className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <img
          src={photo.url}
          alt={altFor(photo)}
          className="mx-auto max-h-[78vh] w-auto rounded-xl object-contain"
          {...protectImage}
        />
        {(photo.title || photo.description) && (
          <figcaption className="mx-auto mt-4 max-w-[65ch] text-center">
            {photo.title && <p className="text-lg font-semibold text-slate-50">{photo.title}</p>}
            {photo.description && <p className="mt-1 text-slate-300">{photo.description}</p>}
            {photo.category && <p className="mt-2 font-mono text-xs text-slate-400">{photo.category}</p>}
          </figcaption>
        )}
      </figure>
    </div>
  );
};

const GalleryPage = ({ isAuthenticated, onSave, onDelete, images }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showUploadModal, setShowUploadModal] = useState(false);

  const filteredPhotos =
    selectedCategory === "All"
      ? images
      : images.filter((photo) => photo.category === selectedCategory);

  const handleDelete = (photo) => {
    if (window.confirm("Are you sure you want to delete this photo?")) {
      onDelete(photo.id, photo.url, photo.thumbUrl);
    }
  };

  return (
    <div className="page py-12 md:py-16">
      <header className="flex flex-col items-start gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="page-title">Photo Gallery</h1>
          <p className="mt-3 text-slate-400">Behind the scenes and memorable moments</p>
        </div>
        {isAuthenticated && (
          <button type="button" onClick={() => setShowUploadModal(true)} className="btn-primary">
            <Plus size={18} strokeWidth={1.75} />
            Upload Photo
          </button>
        )}
      </header>

      <div className="mt-8 flex flex-wrap gap-2" role="group" aria-label="Filter by category">
        {CATEGORIES.map((category) => {
          const active = selectedCategory === category;
          return (
            <button
              key={category}
              type="button"
              onClick={() => setSelectedCategory(category)}
              aria-pressed={active}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150 ease-out ${
                active
                  ? "bg-slate-50 text-slate-950"
                  : "text-slate-300 ring-1 ring-inset ring-white/15 hover:text-slate-50 hover:ring-white/30"
              }`}
            >
              {category}
            </button>
          );
        })}
      </div>

      {filteredPhotos.length === 0 ? (
        <p className="mt-12 text-slate-400">
          {images.length === 0
            ? isAuthenticated
              ? "No photos yet. Use 'Upload Photo' to add the first one."
              : "No photos yet. Check back soon."
            : `No photos in "${selectedCategory}" yet.`}
        </p>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
          {filteredPhotos.map((photo) => (
            <Reveal key={photo.id} as="figure">
              <button
                type="button"
                onClick={() => setSelectedPhoto(photo)}
                aria-label={`View ${altFor(photo)}`}
                className="block aspect-[4/3] w-full overflow-hidden rounded-xl bg-slate-900"
              >
                <img
                  src={photo.thumbUrl || photo.url}
                  alt={altFor(photo)}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                  {...protectImage}
                />
              </button>
              <figcaption className="mt-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {photo.title && <p className="break-words font-medium text-slate-100">{photo.title}</p>}
                  {photo.description && (
                    <p className="mt-1 line-clamp-2 text-sm text-slate-400">{photo.description}</p>
                  )}
                  {photo.category && <p className="mt-1 font-mono text-xs text-slate-400">{photo.category}</p>}
                </div>
                {isAuthenticated && (
                  <button type="button" onClick={() => handleDelete(photo)} className="icon-btn-danger" aria-label="Delete photo">
                    <Trash2 size={16} strokeWidth={1.75} />
                  </button>
                )}
              </figcaption>
            </Reveal>
          ))}
        </div>
      )}

      <p className="mt-10 font-mono text-xs text-slate-400">
        Showing {filteredPhotos.length} {filteredPhotos.length === 1 ? "photo" : "photos"}
        {selectedCategory !== "All" && ` in "${selectedCategory}"`}
      </p>

      {selectedPhoto && <Lightbox photo={selectedPhoto} onClose={() => setSelectedPhoto(null)} />}

      <UploadPhotoModal open={showUploadModal} onClose={() => setShowUploadModal(false)} onSave={onSave} />
    </div>
  );
};

export default GalleryPage;
